const LOG_PREFIX = "[Firebase Remote Config]";
const REMOTE_CONFIG_API_NAME = "ARZZFirebaseRemoteConfig";
const AB_TESTS_API_NAME = "ARZZ_AB_TESTS";
const CACHE_KEY_PREFIX = "ARZZ_AB_TESTS:";
const CACHE_TTL = 3600000;
const TRACKING_STORAGE_KEY = "ARZZ_AB_TESTS:tracked";
const DECLARED_TEST_SELECTOR = "[data-ab-test]";

const abTests = (window[AB_TESTS_API_NAME] = window[AB_TESTS_API_NAME] || {});
const pendingVariants = {};

abTests.getVariant = function getVariant(parameterName, defaultValue = "0") {
  if (!parameterName) {
    return Promise.resolve(String(defaultValue));
  }

  if (typeof abTests[parameterName] === "string") {
    return Promise.resolve(abTests[parameterName]);
  }

  if (!pendingVariants[parameterName]) {
    setLoadingClass(parameterName);
    pendingVariants[parameterName] = loadVariant(parameterName, defaultValue);
  }

  return pendingVariants[parameterName];
};

abTests.initTest = function initTest(parameterName) {
  window[AB_TESTS_API_NAME].getVariant(parameterName).then(function (variant) {
    const experiment = { ab_test_name: parameterName, ab_test_variant: variant };

    const alreadyInDataLayer = (window.dataLayer || []).some(
      (entry) => entry.event === "firebase_ab_test_init" && entry.ecommerce?.ab_test_name === parameterName
    );

    if (!alreadyInDataLayer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "firebase_ab_test_init",
        ecommerce: {
          ...experiment,
          current_page_url: window.location.href,
        },
      });
    }

    try {
      const stored = JSON.parse(localStorage.getItem(TRACKING_STORAGE_KEY) || "[]");
      const alreadyStored = stored.some((e) => e.ab_test_name === parameterName);

      if (!alreadyStored) {
        stored.push(experiment);
        localStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(stored));
      }
    } catch (e) {
      console.error(`${LOG_PREFIX} Failed to persist tracking for "${parameterName}".`, e);
    }
  });
};

abTests.getWebConfig = function getWebConfig(parameterName) {
  return window[AB_TESTS_API_NAME].getVariant(parameterName).then(function (value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  });
};

abTests.refreshDeclaredTests = function refreshDeclaredTests(root) {
  initDeclaredTests(root || document);
};

abTests.debug = function debug() {
  const tests = getDeclaredTestElements(document).map(function (element) {
    const parameterName = element.getAttribute("data-ab-test");

    return {
      parameterName,
      defaultValue: element.getAttribute("data-ab-test-default") || "0",
      tracked: element.hasAttribute("data-ab-test-track"),
      value: abTests[parameterName],
      cachedValue: getCachedVariant(parameterName),
    };
  });

  return {
    apiReady: typeof abTests.getVariant === "function",
    remoteConfigReady: Boolean(window[REMOTE_CONFIG_API_NAME]?.getVariant),
    htmlClasses: Array.from(document.documentElement.classList).filter(function (className) {
      return className.includes("abtesting") || className.includes("ab-test");
    }),
    tests,
  };
};

initDeclaredTests(document);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", function () {
    initDeclaredTests(document);
  }, { once: true });
}

document.addEventListener("shopify:section:load", function (event) {
  initDeclaredTests(event.target);
});

async function loadVariant(parameterName, defaultValue = "0") {
  const fallbackValue = String(defaultValue);
  const cachedVariant = getCachedVariant(parameterName);

  if (cachedVariant !== null) {
    setVariant(parameterName, cachedVariant);
  }

  try {
    const remoteConfigApi = await waitForRemoteConfigApi();
    const variant = await remoteConfigApi.getVariant(parameterName, fallbackValue);
    const variantValue = String(variant ?? fallbackValue);

    setVariant(parameterName, variantValue);
    refreshAfterActivation(remoteConfigApi, parameterName, variantValue);

    return variantValue;
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to load A/B test "${parameterName}".`, error);

    if (cachedVariant === null) {
      setVariant(parameterName, fallbackValue);
    }

    return fallbackValue;
  }
}

function waitForRemoteConfigApi() {
  const remoteConfigApi = window[REMOTE_CONFIG_API_NAME];

  if (remoteConfigApi && typeof remoteConfigApi.getVariant === "function") {
    return Promise.resolve(remoteConfigApi);
  }

  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 100;

    const intervalId = window.setInterval(() => {
      const api = window[REMOTE_CONFIG_API_NAME];

      attempts += 1;

      if (api && typeof api.getVariant === "function") {
        window.clearInterval(intervalId);
        resolve(api);
        return;
      }

      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        reject(new Error(`${REMOTE_CONFIG_API_NAME}.getVariant is not available.`));
      }
    }, 50);
  });
}

function setVariant(parameterName, variant) {
  const classPrefix = normalizeClassPart(parameterName);
  const isAlreadyReady = document.documentElement.classList.contains(`${classPrefix}-ready`);
  const previousVariant = abTests[parameterName];

  abTests[parameterName] = variant;
  setCachedVariant(parameterName, variant);

  if (isAlreadyReady && previousVariant === variant) {
    return;
  }

  updateDocumentClasses(parameterName, variant);
  dispatchReadyEvent(parameterName, variant);
}

function updateDocumentClasses(parameterName, variant) {
  const classPrefix = normalizeClassPart(parameterName);
  const variantClassPrefix = `${classPrefix}-variant-`;
  const html = document.documentElement;

  Array.from(html.classList).forEach((className) => {
    if (className.startsWith(variantClassPrefix)) {
      html.classList.remove(className);
    }
  });

  html.classList.remove(`${classPrefix}-loading`);
  html.classList.add(`${variantClassPrefix}${normalizeClassPart(variant)}`);
  html.classList.add(`${classPrefix}-ready`);
}

function dispatchReadyEvent(parameterName, variant) {
  const createEvent = () =>
    new CustomEvent("ab-test-ready", {
      detail: {
        parameter: parameterName,
        variant,
      },
    });

  document.dispatchEvent(createEvent());

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => document.dispatchEvent(createEvent()), { once: true });
  }
}

async function refreshAfterActivation(remoteConfigApi, parameterName, currentVariant) {
  if (!remoteConfigApi || typeof remoteConfigApi.waitForActivation !== "function") {
    return;
  }

  try {
    await remoteConfigApi.waitForActivation();

    const variant = await remoteConfigApi.getVariant(parameterName, currentVariant);
    const variantValue = String(variant ?? currentVariant);

    if (variantValue !== currentVariant) {
      setVariant(parameterName, variantValue);
    }
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to refresh A/B test "${parameterName}".`, error);
  }
}

function setLoadingClass(parameterName) {
  document.documentElement.classList.add(`${normalizeClassPart(parameterName)}-loading`);
}

function initDeclaredTests(root) {
  const testElements = getDeclaredTestElements(root);

  testElements.forEach(function (element) {
    const parameterName = element.getAttribute("data-ab-test");
    const defaultValue = element.getAttribute("data-ab-test-default") || "0";

    if (!parameterName) {
      return;
    }

    abTests.getVariant(parameterName, defaultValue);

    if (element.hasAttribute("data-ab-test-track")) {
      abTests.initTest(parameterName);
    }
  });
}

function getDeclaredTestElements(root) {
  if (!root) {
    return [];
  }

  const elements = [];

  if (typeof root.matches === "function" && root.matches(DECLARED_TEST_SELECTOR)) {
    elements.push(root);
  }

  if (typeof root.querySelectorAll === "function") {
    elements.push(...root.querySelectorAll(DECLARED_TEST_SELECTOR));
  }

  return elements;
}

function getCachedVariant(parameterName) {
  try {
    const cachedValue = window.localStorage.getItem(`${CACHE_KEY_PREFIX}${parameterName}`);

    if (!cachedValue) {
      return null;
    }

    const cacheEntry = JSON.parse(cachedValue);

    if (!cacheEntry || Date.now() - cacheEntry.timestamp > CACHE_TTL) {
      return null;
    }

    return typeof cacheEntry.variant === "string" ? cacheEntry.variant : null;
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to read cached A/B test "${parameterName}".`, error);
    return null;
  }
}

function setCachedVariant(parameterName, variant) {
  try {
    window.localStorage.setItem(
      `${CACHE_KEY_PREFIX}${parameterName}`,
      JSON.stringify({
        variant,
        timestamp: Date.now(),
      }),
    );
  } catch (error) {
    console.error(`${LOG_PREFIX} Failed to cache A/B test "${parameterName}".`, error);
  }
}

function normalizeClassPart(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
